/**
 * Search-param codec for the app router.
 *
 * TanStack Router's default `parseSearch` JSON-parses first-level values, so
 * `?id=123` becomes the number 123 and `?paymentSuccess=true` a boolean. Every
 * page in this app was written against Next's `router.query`, where values are
 * strings (or string[] for a repeated key). This codec keeps that shape.
 */
export function parseSearchAsStrings(searchStr: string): Record<string, string | string[]> {
  const params = new URLSearchParams(searchStr.startsWith('?') ? searchStr.slice(1) : searchStr)
  const out: Record<string, string | string[]> = {}
  for (const [key, value] of params) {
    const existing = out[key]
    if (existing === undefined) out[key] = value
    else if (Array.isArray(existing)) existing.push(value)
    else out[key] = [existing, value]
  }
  return out
}

export function stringifySearchFromStrings(search: Record<string, unknown>): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(search)) {
    if (value === undefined || value === null) continue
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, String(item))
    } else {
      params.append(key, String(value))
    }
  }
  const encoded = params.toString()
  return encoded ? `?${encoded}` : ''
}

export function queryToStrings(
  query: Record<string, string | number | boolean | undefined> | undefined,
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined) continue
    out[key] = String(value)
  }
  return out
}
